import React from 'react';
import { FolderIcon } from '@heroicons/react/24/solid';
import { getRoleMetadata, parseGroup } from '../../config/roleMetadata';

/**
 * Renders a single colored role badge for a parsed `project:role` group string.
 *
 * Presentational only — no data fetching or state. Shows the project name (when
 * present) followed by a colored, icon-prefixed badge for the role.
 *
 * @param {object} props
 * @param {string} props.group - Raw group string (e.g. `vertuo:developer` or `rootAccess`).
 * @param {boolean} [props.showProject=true] - Whether to show the project prefix.
 * @param {string} [props.size='md'] - `sm` or `md` sizing.
 */
export default function RoleBadge({ group, showProject = true, size = 'md' }) {
  const { project, role } = parseGroup(group);
  const meta = getRoleMetadata(role);
  const { Icon } = meta;

  const sizing =
    size === 'sm'
      ? 'text-[11px] px-2 py-0.5 gap-1'
      : 'text-xs px-2.5 py-1 gap-1.5';
  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';

  return (
    <span
      className="inline-flex items-center gap-1 transition-transform hover:-translate-y-px"
      title={meta.description}
    >
      {showProject && project && (
        <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-gray-50 to-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600 border border-gray-200 ring-1 ring-gray-200/50 shadow-sm">
          <FolderIcon className="h-3 w-3 text-indigo-400" />
          {project}
        </span>
      )}
      <span
        className={`inline-flex items-center rounded-full font-semibold tracking-wide ${sizing} ${meta.color.badge}`}
      >
        <Icon className={iconSize} />
        {meta.label}
      </span>
    </span>
  );
}
